import React, { createContext, useContext, useReducer, useRef } from "react";
import axios from "axios";
import PropTypes from "prop-types";

const initialState = {
  isSubmitting: false,
  showResults: false,
  searchUrl: null,
  error: null,
};

const SEARCH_START = "SEARCH_START";
const SEARCH_SUCCESS = "SEARCH_SUCCESS";
const SEARCH_ERROR = "SEARCH_ERROR";
const CLOSE_RESULTS = "CLOSE_RESULTS";

const searchReducer = (state, action) => {
  switch (action.type) {
    case SEARCH_START:
      return {
        ...state,
        isSubmitting: true,
        showResults: true,
        searchUrl: null,
        error: null,
      };
    case SEARCH_SUCCESS:
      return {
        ...state,
        isSubmitting: false,
        searchUrl: action.payload,
        error: null,
      };
    case SEARCH_ERROR:
      return {
        ...state,
        isSubmitting: false,
        showResults: false,
        searchUrl: null,
        error: action.payload,
      };
    case CLOSE_RESULTS:
      return {
        ...state,
        showResults: false,
        searchUrl: null,
        error: null,
      };
    default:
      return state;
  }
};

const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const cancelTokenRef = useRef(null);

  const performSearch = async (searchData) => {
    dispatch({ type: SEARCH_START });

    try {
      const formatDate = (date) => {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
      };

      const requestBody = {
        origin: searchData.origin,
        destination: searchData.destination,
        fromDate: formatDate(searchData.departDate),
        toDate: formatDate(searchData.returnDate),
        clazz: searchData.class,
        airlines: searchData.airline === "all" ? "" : searchData.airline,
        adults: parseInt(searchData.adults, 10),
        childs: parseInt(searchData.children, 10),
        infants: parseInt(searchData.infants, 10),
        companyCode: searchData.companyCode || "BS600",
        sessionId: searchData.sessionId || "0",
        isReturn: searchData.tripType === "return",
        isDirectFlight: searchData.directFlightsOnly,
        isPlusMinusOneDay: searchData.flexibleDates,
        isPlusMinusThreeDay: false,
      };

      const response = await axios.post(
        "/travelservice/travelservice/searchflight",
        requestBody
      );

      const responseUrl = response.data;

      const isValidUrl = (urlString) => {
        const urlPattern =
          /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
        if (
          urlString === "https://www.btres.com/FailedRes?_@=Response Unavail"
        ) {
          return false;
        }
        return (
          urlPattern.test(urlString) ||
          urlString.startsWith("https://www.btres.com/")
        );
      };

      if (isValidUrl(responseUrl)) {
        dispatch({ type: SEARCH_SUCCESS, payload: responseUrl });
        return responseUrl;
      } else {
        throw new Error("Invalid search results URL");
      }
    } catch (error) {
      dispatch({
        type: SEARCH_ERROR,
        payload: "No flights found for the selected criteria.",
      });
      throw error;
    }
  };

  const closeResults = () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Search closed by user.");
    }
    dispatch({ type: CLOSE_RESULTS });
  };

  return (
    <SearchContext.Provider
      value={{
        state,
        performSearch,
        closeResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

SearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};
